<template>
  <div>
    <l-map ref="thePickerMap"
    :zoom.sync="currentZoom"
    :options="mapOptions"
    :center="center"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :bounds.sync="bounds"
    :style="'height: ' + height + '; width: 100%;'"
    @ready="emitReady"
    @click="pickPlace($event.latlng.lat, $event.latlng.lng)"
    >
      <l-tile-layer
        v-for="tileProvider in tileProvidersLocal"
        :key="tileProvider.name"
        :name="tileProvider.name"
        :visible="tileProvider.visible"
        :url="tileProvider.url"
        :attribution="tileProvider.attribution"
        layer-type="base"
        :options="tileProvider.options"
        v-if="tileProvidersReady"
      />
      <l-control-layers
        :position="layersPosition"
        :collapsed="false"
        :sort-layers="true"
      />
      <l-control-zoom :position="zoomPosition" />
      <l-control-attribution
        :position="attributionPosition"
        prefix="Moova"
      />
      <l-control-scale position="bottomright" :imperial="imperial" :metric="metric" />

      <map-marker
        v-for="marker in markers"
        :marker="marker"
        :key="marker.id"
        :lat-lng="[marker.lat, marker.lng]"
        :visible="true"
        :draggable="false"
        img
      >
        <l-icon
          :iconUrl="require('leaflet/dist/images/marker-icon.png')"
        >
        </l-icon>
      </map-marker>
    </l-map>
    <form @submit.prevent="emitPlacePicked" class="my-3">
      <div class="form-group col-sm-12" v-if="hasReference">
        <label for="reference" v-translate>Place Reference</label>
        <div class="input-group">
          <b-form-textarea
            id="reference"
            v-model="reference"
            rows="2"
            max-rows="3"
          ></b-form-textarea>
        </div>
        <b-form-text v-translate>Please add a brief description of the place.</b-form-text>
      </div>
      <div class="form-group col-12 form-actions">
        <b-button variant="secondary" type="submit" :disabled="this.markers.findIndex(x => x.id === '1') === -1" v-translate>
          Confirm Place
        </b-button>
        <b-button variant="outline-secondary" size="md" @click.prevent="emitCancel" v-translate>
          Cancel
        </b-button>
      </div>
    </form>
  </div>
</template>
<script>
import AddressesMixin from '@/mixins/address.mixin'
import {
  LMap,
  LTileLayer,
  LMarker,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import MapMarker from '@/components/Maps/MapMarker'

export default {
  name: 'PlacePicker',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    MapMarker
  },
  data() {
    return {
      reference: '',
      icon: null,
      places: [],
      currentPlace: null,
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: this.zoom || 11,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      metric: false,
      markers: [],
      bounds: null,
      fitted: false,
      tileProvidersReady: false,
      tileProvidersLocal: []
    }
  },
  props: {
    geolocate: {
      type: Boolean,
      default: false
    },
    fitOnce: {
      type: Boolean,
      default: false
    },
    fitAlways: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 11
    },
    centerMap: {
      type: Boolean,
      default: false
    },
    hasReference: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '300px'
    },
    visualReference: {
      type: Object,
      default: () => {}
    }
  },
  watch: {
    tileProviders: {
      handler(newVal, oldVal) {
        this.tileProvidersLocal = JSON.parse(JSON.stringify(newVal))
        this.setDefaultTileProvider()
      },
      deep: true
    }
  },
  mixins: [
    AddressesMixin
  ],
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial
    await this.checkMapProviders()
    this.tileProvidersLocal = JSON.parse(JSON.stringify(this.tileProviders))
    this.setDefaultTileProvider()
    if (this.visualReference) {
      this.markers = [
        this.transformMarker(
          {
            id: '0',
            lat: this.visualReference.lat,
            lng: this.visualReference.lng,
            icon: this.visualReference.icon,
            x: 10,
            y: 10
          }
        )
      ]
    }
  },
  beforeDestroy() {
    this.tileProvidersLocal = []
    this.map = null
  },
  methods: {
    setDefaultTileProvider() {
      if (this.ecosystemMaps && this.ecosystemMaps !== null && this.ecosystemMaps.defaultTileProvider && this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
        this.tileProvidersLocal = this.tileProvidersLocal.map(x => {
          if (x.name === this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
      }
      this.tileProvidersReady = true
    },
    pickPlace(lat, lng) {
      this.center = [lat, lng]
      if (this.markers.findIndex(x => x.id === '0') !== -1) {
        this.markers[1] = this.transformMarker({
          id: '1',
          lat: lat,
          lng: lng,
          icon: {
            type: 'map-marker-alt',
            size: [27, 36],
            anchor: [18, 39],
            color: '#dc3545',
            text: ''
          },
          x: 11,
          y: 34
        })
      } else {
        this.markers = [this.transformMarker({
          id: '1',
          lat: lat,
          lng: lng,
          icon: {
            type: 'map-marker-alt',
            size: [27, 36],
            anchor: [18, 39],
            color: '#dc3545',
            text: ''
          },
          x: 11,
          y: 34
        })]
      }
    },
    async emitPlacePicked() {
      await this.checkPlaceByCoords(this.markers.find(x => x.id === '1').position.lat, this.markers.find(x => x.id === '1').position.lng)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.theCheckedAddress.reference = this.reference
          this.$emit('placePicked', this.theCheckedAddress)
        }
      })
    },
    emitCancel() {
      this.$emit('cancel')
    },
    emitReady() {
      this.map = this.$refs.thePickerMap.mapObject
      this.map.on('popupclose', () => this.emitCourier(null))
      this.mapReady = true
      this.$emit('ready', true)
      this.setDefaultTileProvider()
      setTimeout(function () {
        this.map.invalidateSize()
      }.bind(this), 100)
    },
    transformMarker(item) {
      return {
        id: item.id,
        position: {
          lat: item.lat,
          lng: item.lng
        },
        icon: typeof item.icon === 'string' ? {
          iconUrl: item.icon,
          iconAnchor: item.anchor || [item.x, item.y],
          iconSize: item.size || [item.x * 2, item.y * 2],
          tooltipAnchor: item.tooltipAnchor || [item.x, 0],
          popupAnchor: item.popupAnchor || [0, -item.y]
        } : {
          iconType: item.icon.type,
          iconAnchor: item.icon.anchor,
          iconSize: item.icon.size,
          tooltipAnchor: item.icon.tooltipAnchor || [item.x, 0],
          popupAnchor: item.icon.popupAnchor || [0, -item.y],
          iconColor: item.icon.color,
          iconText: item.icon.text,
          iconRotation: item.icon.direction ? (item.icon.direction <= 180 ? item.icon.direction : item.icon.direction - 360) : 0
        },
        infoText: item.infoText,
        tooltip: item.tooltip || null,
        popup: item.popup || null
      }
    }
  }
}

</script>
